import type React from "react";

import styled from "styled-components";

interface Props {
  readonly audioLevel: number; // 0-10 scale
  readonly position?: "local" | "remote";
  readonly hasWarning?: boolean;
}

export const AudioLevelIndicator: React.FC<Props> = ({ audioLevel, position = "remote", hasWarning = false }) => {
  // Create 5 bars, each representing 2 levels (0-2, 2-4, 4-6, 6-8, 8-10)
  const bars = [
    { threshold: 0, height: 4 },
    { threshold: 2, height: 6 },
    { threshold: 4, height: 8 },
    { threshold: 6, height: 10 },
    { threshold: 8, height: 12 },
  ];

  return (
    <IndicatorContainer $position={position} $hasWarning={hasWarning} key={position}>
      {hasWarning && <WarningIcon>⚠️</WarningIcon>}
      <BarsContainer>
        {bars.map(bar => (
          <Bar key={bar.threshold} $active={audioLevel > 0 && audioLevel >= bar.threshold} $height={bar.height} />
        ))}
      </BarsContainer>
    </IndicatorContainer>
  );
};

const IndicatorContainer = styled.div<{ $position: "local" | "remote"; $hasWarning: boolean }>`
  position: absolute;
  ${props => (props.$position === "local" ? "bottom: 6px; left: 6px;" : "bottom: 6px; right: 6px;")}
  display: flex;
  align-items: center;
  gap: 3px;
  background: ${props => (props.$hasWarning ? "rgba(255, 152, 0, 0.9)" : "rgba(0, 0, 0, 0.6)")};
  padding: 3px 5px;
  border-radius: 3px;
  z-index: 10;
  backdrop-filter: blur(4px);
  transition: background 0.3s ease;
`;

const BarsContainer = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 1px;
`;

const Bar = styled.div<{ $active: boolean; $height: number }>`
  width: 2px;
  height: ${props => props.$height}px;
  background: ${props => (props.$active ? "#00c853" : "rgba(255, 255, 255, 0.3)")};
  border-radius: 1px;
  transition: all 0.1s ease;
`;

const WarningIcon = styled.div`
  font-size: 10px;
  line-height: 1;
`;
